import { runPreBootstrapAuthGate, setPreInitedKeycloak } from '@core/auth/keycloak-pre-bootstrap.service';

// Log version information at application startup
const logVersionInfo = (): void => {
  const version = window.__env?.appVersion || 'dev';
  const commit = window.__env?.appCommit || 'unknown';
  const buildDate = window.__env?.appBuildDate || 'unknown';

  console.log('%c============================================', 'color: #4CAF50; font-weight: bold;');
  console.log('%cHQDP UI Application', 'color: #2196F3; font-weight: bold; font-size: 14px;');
  console.log(`%c  Version:    ${version}`, 'color: #9E9E9E;');
  console.log(`%c  Commit:     ${commit}`, 'color: #9E9E9E;');
  console.log(`%c  Build Date: ${buildDate}`, 'color: #9E9E9E;');
  console.log('%c============================================', 'color: #4CAF50; font-weight: bold;');
};

logVersionInfo();

/**
 * HQDP-784 (solution 2): decide authentication BEFORE downloading the Angular app
 * bundle.
 *
 * `runPreBootstrapAuthGate()` initializes keycloak-js up front. Under
 * `onLoad: 'login-required'`, an unauthenticated user is redirected to the
 * Keycloak login page WITHOUT ever loading the multi-MB app chunks. Only once
 * authenticated (or when the gate does not apply — Keycloak disabled / offline /
 * localhost devToken) do we dynamically import + bootstrap the app, keeping
 * `app.config` and `AppComponent` (and their heavy transitive graph) OUT of the
 * boot-critical `main.js`.
 */
/**
 * Precharge les deux gros chunks dont l'ÉVALUATION de module ne lit PAS
 * l'instance Keycloak pré-initialisée. `app.config` en est délibérément EXCLU :
 * sa const top-level `appConfig` exécute `getKeycloakProviders()` →
 * `getPreInitedKeycloak()` au moment de l'évaluation, elle ne doit donc être
 * importée qu'APRÈS `setPreInitedKeycloak()`. L'importer ici (en parallèle, avant
 * la résolution du gate) perd la course et fait exécuter à l'app un SECOND
 * `keycloak.init({ onLoad: 'login-required' })` → un `?state=` redondant qui peut
 * boucler indéfiniment sur une session froide. Cf. docs/analysis + HQDP-784.
 */
function preloadNonConfigChunks() {
  return Promise.all([import('@angular/platform-browser'), import('./app/app.component')]);
}

async function bootstrap(): Promise<void> {
  // HQDP-784 (Levier 2): sur le retour de Keycloak (`?code` présent) on précharge
  // les gros chunks EN PARALLÈLE de l'échange de token (`keycloak.init()`), au lieu
  // d'attendre sa résolution. Sur la première visite (pas de `?code`) on ne
  // précharge PAS : un non-authentifié est redirigé et ne doit pas télécharger l'app.
  // `app.config` est chargé À PART, APRÈS `setPreInitedKeycloak()`, pour éviter la
  // double-init Keycloak (double `?state=`, cf. preloadNonConfigChunks).
  const isReturnTrip = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('code');
  const chunksPromise = isReturnTrip ? preloadNonConfigChunks() : null;

  const preInited = await runPreBootstrapAuthGate();
  if (preInited) {
    // Hand the already-initialized instance to app.config's getKeycloakProviders().
    // MUST run before app.config is imported (its `appConfig` const reads this).
    setPreInitedKeycloak(preInited);
  }

  const [[{ bootstrapApplication }, { AppComponent }], { appConfig }] = await Promise.all([
    chunksPromise ?? preloadNonConfigChunks(),
    import('./app/app.config'),
  ]);

  await bootstrapApplication(AppComponent, appConfig);
}

bootstrap().catch((err) => console.error(err));
